import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Mail, Pencil, Trash2, Send, Loader2 } from 'lucide-react';

const EmailTemplates = ({ setAuth }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [templateToSend, setTemplateToSend] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTemplates();
    createDefaultTemplates();
  }, []);

  const createDefaultTemplates = async () => {
    try {
      const response = await api.get('/email-templates');
      // Only create defaults if no templates exist
      if (response.data.length === 0) {
        const defaultTemplates = [
          {
            name: 'New Business - IT Services',
            subject: 'Welcome to Your New Business Journey - IT Support & Services',
            body: `Good {time_greeting} {director_name},

Congratulations on setting up your new business in the {sic_name} sector - exciting times coming your way! We would like to help take away some of the stress with our business services.

Below is an overview of our IT Support packages and additional services we can provide:

🔹 IT Support Packages

• Essentials – Proactive monitoring, remote support, and updates
• Business – Includes everything in Essentials plus onsite visits, priority response, and patch management
• Premium – Fully managed IT support with 24/7 monitoring, security management, and vCIO consultancy

🔹 Additional Services

• VoIP Phone Systems (cloud-based and onsite)
• Broadband & Connectivity (FTTP, SOGEA, Leased Lines)
• Web Design & Hosting
• Microsoft 365 & Cloud Solutions
• Cyber Security & Backup Services

We work with other {sic_name} businesses and a range of small and medium businesses across Staffordshire and Cheshire, providing fast response times and a friendly, personal service — without the call queues.

I'd be happy to prepare a tailored proposal once you've had a chance to review our services.

Kind regards,
Karl Beardmore
Managing Director | BitzUK
📞 01782 478884
📧 karl@bitzuk.com
🌐 www.bitzuk.com`
          },
          {
            name: 'New Business - Accounting Services',
            subject: 'Congratulations on Your New Business - Accounting Support Available',
            body: `Good {time_greeting} {director_name},

Congratulations on launching {company_name}! Starting a business in the {sic_name} sector is an exciting venture.

As you focus on growing your business, we'd like to help you stay on top of your finances with our comprehensive accounting services:

📊 Our Services:

• Bookkeeping & VAT Returns
• Annual Accounts & Tax Returns
• Payroll Management
• Management Accounts & Financial Planning
• Company Formation & Registration Support
• Tax Planning & Advisory

We specialise in working with new businesses in the {sic_name} sector and understand the unique challenges you face.

Our packages start from just £99/month and include:
✓ Dedicated accountant
✓ Cloud accounting software (Xero/QuickBooks)
✓ Unlimited email support
✓ Quarterly business reviews

Would you like to schedule a free 30-minute consultation to discuss how we can support your business?

Best regards,
[Your Name]
[Your Company]
[Your Contact Details]`
          },
          {
            name: 'Follow-up - Initial Contact',
            subject: 'Following Up - {company_name}',
            body: `Good {time_greeting} {director_name},

I hope this email finds you well. I wanted to follow up on my previous email regarding our services for {company_name}.

I understand you're likely busy getting your new {sic_name} business off the ground, so I'll keep this brief.

Would you be open to a quick 15-minute call to discuss how we can support your business goals? I'm happy to work around your schedule.

You can book a time directly here: [Your Booking Link]

Or simply reply with a convenient time for you.

Looking forward to connecting!

Best regards,
[Your Name]
[Your Position]
[Your Contact Details]`
          },
          {
            name: 'New Business - Marketing Services',
            subject: 'Launch Your {sic_name} Business with Professional Marketing',
            body: `Good {time_greeting} {director_name},

Congratulations on establishing {company_name}! 

We noticed your recent company formation in the {sic_name} sector and wanted to reach out with an exclusive offer for new businesses.

🚀 New Business Marketing Package:

• Professional Website Design & Development
• SEO Optimisation
• Google Business Profile Setup
• Social Media Management (3 platforms)
• Email Marketing Setup
• Logo & Brand Identity Design

Special Launch Offer: 30% OFF for companies formed in the last 90 days!

Package starts at just £499 (normally £750)

Why wait? The sooner you establish your online presence, the faster you'll attract customers.

Book a free strategy call: [Your Link]

Best regards,
[Your Name]
[Your Agency]
[Your Contact Details]`
          }
        ];

        for (const template of defaultTemplates) {
          await api.post('/email-templates', template);
        }
        
        // Refresh templates after creating defaults
        fetchTemplates();
      }
    } catch (error) {
      // Silently fail if templates already exist
      console.log('Default templates check completed');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/email-templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingTemplate) {
        await api.put(`/email-templates/${editingTemplate.id}`, { name, subject, body });
        toast.success('Template updated successfully!');
      } else {
        await api.post('/email-templates', { name, subject, body });
        toast.success('Template created successfully!');
      }
      setShowDialog(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      await api.delete(`/email-templates/${templateToDelete.id}`);
      toast.success('Template deleted successfully!');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!templateToSend) return;

    setSending(true);
    try {
      await api.post('/send-email', {
        to_email: recipientEmail,
        subject: templateToSend.subject,
        body: templateToSend.body
      });
      toast.success('Email sent successfully!');
      setSendDialogOpen(false);
      setTemplateToSend(null);
      setRecipientEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setName(template.name);
    setSubject(template.subject);
    setBody(template.body);
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setName('');
    setSubject('');
    setBody('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="border-slate-300"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Email Templates</h1>
                <p className="text-sm text-slate-500">Manage your email templates</p>
              </div>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setShowDialog(true);
              }}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md"
              data-testid="add-template-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : templates.length === 0 ? (
          <Card className="shadow-sm border-slate-200" data-testid="empty-state">
            <CardContent className="py-20 text-center">
              <Mail className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No templates yet</h3>
              <p className="text-slate-500 mb-6">Create your first email template to get started</p>
              <Button
                onClick={() => {
                  resetForm();
                  setShowDialog(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-lg border-slate-200 bg-white" data-testid={`template-card-${template.id}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold text-slate-800 line-clamp-1">
                    {template.name}
                  </CardTitle>
                  <CardDescription className="line-clamp-1">{template.subject}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 line-clamp-3 mb-4">{template.body}</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setTemplateToSend(template);
                        setSendDialogOpen(true);
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid={`send-template-${template.id}`}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Send
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(template)}
                      className="border-slate-300"
                      data-testid={`edit-template-${template.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setTemplateToDelete(template);
                        setDeleteDialogOpen(true);
                      }}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`delete-template-${template.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Template Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-2xl" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingTemplate ? 'Edit Template' : 'New Template'}
            </DialogTitle>
            <DialogDescription>Create or edit an email template</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name</Label>
                <Input
                  id="name"
                  data-testid="template-name-input"
                  placeholder="Welcome Email"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Email Subject</Label>
                <Input
                  id="subject"
                  data-testid="template-subject-input"
                  placeholder="Welcome to our service"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  className="border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="body">Email Body</Label>
                <Textarea
                  id="body"
                  data-testid="template-body-input"
                  placeholder="Enter your email content here..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  required
                  rows={10}
                  className="border-slate-300 resize-none"
                />
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-xs font-semibold text-blue-900 mb-1">Available Dynamic Fields:</p>
                  <div className="text-xs text-blue-800 space-y-1">
                    <p><code className="bg-blue-100 px-1 rounded">{'{time_greeting}'}</code> - Morning/Afternoon/Evening</p>
                    <p><code className="bg-blue-100 px-1 rounded">{'{director_name}'}</code> - Director's first name</p>
                    <p><code className="bg-blue-100 px-1 rounded">{'{director_full_name}'}</code> - Director's full name</p>
                    <p><code className="bg-blue-100 px-1 rounded">{'{company_name}'}</code> - Company name</p>
                    <p><code className="bg-blue-100 px-1 rounded">{'{sic_name}'}</code> - Business sector name</p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  resetForm();
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-template-button"
              >
                {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-template-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{templateToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTemplate}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-template-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Email Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={(open) => {
        setSendDialogOpen(open);
        if (!open) {
          setTemplateToSend(null);
          setRecipientEmail('');
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Send Email</DialogTitle>
            <DialogDescription>Send this template to a recipient</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendEmail}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="recipient">Recipient Email</Label>
                <Input
                  id="recipient"
                  data-testid="recipient-email-input"
                  type="email"
                  placeholder="recipient@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  required
                  className="border-slate-300"
                />
              </div>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Subject:</p>
                <p className="text-sm text-slate-600">{templateToSend?.subject}</p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSendDialogOpen(false);
                }}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-send-email-button"
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates;
